import { CIVS } from '../../gameplay/civilisations';
import { KINDS } from '../../gameplay/registry';
import { C } from '../../gameplay/types';
import type { SimEvent, World } from '../simulation/world';

export interface Volumes {
  master: number;
  music: number;
  effects: number;
  voices: number;
  ambient: number;
}

/** Musical scales (semitone offsets) chosen per civilisation for generative music. */
const SCALES: Record<string, number[]> = {
  english: [0, 2, 4, 5, 7, 9, 11],
  french: [0, 2, 4, 7, 9, 11, 14],
  germans: [0, 2, 3, 5, 7, 8, 10],
  spanish: [0, 1, 4, 5, 7, 8, 10],
  portuguese: [0, 2, 3, 5, 7, 9, 10],
  ottomans: [0, 1, 4, 5, 7, 8, 11],
  japanese: [0, 1, 5, 7, 8],
  chinese: [0, 2, 4, 7, 9],
  mongols: [0, 3, 5, 7, 10],
  indians: [0, 1, 4, 5, 7, 8, 11],
  aztecs: [0, 3, 5, 7, 10],
  egyptians: [0, 1, 4, 5, 7, 8, 10],
};

/**
 * Procedural audio engine using the Web Audio API. Every sound and the music
 * are synthesised at runtime (no external audio assets). Respects browser
 * autoplay policy: the context is created/resumed on the first user gesture.
 */
export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private bus: Record<keyof Volumes, GainNode> = {} as Record<keyof Volumes, GainNode>;
  private noise!: AudioBuffer;
  private listener = { x: 0, z: 0, d: 26 };
  private lastPlay = new Map<string, number>();
  private musicTimer = 0;
  private musicCiv = 'english';
  private musicStep = 0;
  private ambientNodes: AudioNode[] = [];
  volumes: Volumes = { master: 0.8, music: 0.5, effects: 0.8, voices: 0.7, ambient: 0.5 };

  /** Listeners told when audio becomes available or blocked (for the HUD sound indicator). */
  onState: ((running: boolean) => void) | null = null;

  /** True once the context exists and is actually producing sound. */
  get running(): boolean {
    return this.ctx?.state === 'running';
  }

  /**
   * Create or resume the audio context. Call from a user gesture: browsers only start audio from
   * one. Safari also has an 'interrupted' state (another app took the audio, the Mac slept, a headset
   * changed) that needs the same resume, so anything other than 'running' is resumed.
   */
  unlock(): void {
    if (!this.ctx) {
      const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AC) return;
      // iPhone and iPad: play through the silent switch, like a game or video would.
      const session = (navigator as unknown as { audioSession?: { type: string } }).audioSession;
      if (session) {
        try {
          session.type = 'playback';
        } catch {
          /* older Safari */
        }
      }
      this.ctx = new AC();
      this.ctx.onstatechange = () => this.onState?.(this.running);
      this.master = this.ctx.createGain();
      // Gentle compression keeps big battles from clipping.
      const comp = this.ctx.createDynamicsCompressor();
      comp.threshold.value = -16;
      comp.ratio.value = 4;
      this.master.connect(comp).connect(this.ctx.destination);
      for (const k of ['music', 'effects', 'voices', 'ambient'] as const) {
        this.bus[k] = this.ctx.createGain();
        this.bus[k].connect(this.master);
      }
      const len = this.ctx.sampleRate;
      this.noise = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
      const d = this.noise.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
      this.applyVolumes();
    }
    if (this.ctx.state !== 'running') {
      // Safari only unlocks if a sound actually starts inside the gesture: play one silent sample.
      try {
        const b = this.ctx.createBuffer(1, 1, this.ctx.sampleRate);
        const src = this.ctx.createBufferSource();
        src.buffer = b;
        src.connect(this.ctx.destination);
        src.start(0);
      } catch {
        /* ignore */
      }
      void this.ctx.resume().then(() => this.onState?.(this.running), () => this.onState?.(false));
    }
  }

  /** A short, clear chime for the Settings "Test sound" button. */
  test(): void {
    this.unlock();
    const d = this.out('effects');
    if (d) [523, 659, 784, 1047].forEach((f, i) => this.tone(d, 'triangle', f, f, 0.4, 0.25, i * 0.12));
  }

  setVolumes(v: Volumes): void {
    this.volumes = v;
    this.applyVolumes();
  }

  private applyVolumes(): void {
    if (!this.ctx) return;
    // A missing or corrupt saved setting must never silence (or break) the audio graph.
    const v = (x: unknown, d: number) => (typeof x === 'number' && Number.isFinite(x) ? Math.min(1, Math.max(0, x)) : d);
    this.master.gain.value = v(this.volumes.master, 0.8);
    this.bus.music.gain.value = v(this.volumes.music, 0.5) * 0.5;
    this.bus.effects.gain.value = v(this.volumes.effects, 0.8);
    this.bus.voices.gain.value = v(this.volumes.voices, 0.7);
    this.bus.ambient.gain.value = v(this.volumes.ambient, 0.5) * 0.6;
  }

  setListener(x: number, z: number, d: number): void {
    this.listener = { x, z, d };
  }

  // ---------- Primitive voices ----------
  private out(bus: keyof Volumes, x?: number, z?: number): AudioNode | null {
    const ctx = this.ctx;
    if (!ctx) return null;
    let gain = 1;
    let pan = 0;
    if (x !== undefined && z !== undefined) {
      const dx = x - this.listener.x;
      const dz = z - this.listener.z;
      const dist = Math.sqrt(dx * dx + dz * dz);
      const range = 18 + this.listener.d * 0.8;
      if (dist > range) return null;
      gain = Math.max(0, 1 - dist / range) * (30 / (30 + this.listener.d));
      pan = Math.max(-1, Math.min(1, dx / 18));
    }
    const g = ctx.createGain();
    g.gain.value = gain;
    if (ctx.createStereoPanner) {
      const p = ctx.createStereoPanner();
      p.pan.value = pan;
      g.connect(p).connect(this.bus[bus]);
    } else g.connect(this.bus[bus]);
    return g;
  }

  private throttle(key: string, ms: number): boolean {
    const now = performance.now();
    const t = this.lastPlay.get(key) ?? 0;
    if (now - t < ms) return false;
    this.lastPlay.set(key, now);
    return true;
  }

  private tone(dest: AudioNode, type: OscillatorType, f0: number, f1: number, dur: number, vol: number, delay = 0, attack = 0.005): void {
    const ctx = this.ctx!;
    const t = ctx.currentTime + delay;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol, t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(dest);
    o.start(t);
    o.stop(t + dur + 0.05);
  }

  private noiseBurst(dest: AudioNode, filter: BiquadFilterType, freq: number, q: number, dur: number, vol: number, delay = 0): void {
    const ctx = this.ctx!;
    const t = ctx.currentTime + delay;
    const s = ctx.createBufferSource();
    s.buffer = this.noise;
    s.playbackRate.value = 0.8 + Math.random() * 0.4;
    const f = ctx.createBiquadFilter();
    f.type = filter;
    f.frequency.value = freq;
    f.Q.value = q;
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    s.connect(f).connect(g).connect(dest);
    s.start(t, Math.random() * 0.5);
    s.stop(t + dur + 0.05);
  }

  /** Shaped, filter-swept noise: breaths, whooshes. Rises over `attack`, then falls away. */
  private breath(dest: AudioNode, f0: number, f1: number, q: number, dur: number, vol: number, attack: number, delay = 0): void {
    const ctx = this.ctx!;
    const t = ctx.currentTime + delay;
    const s = ctx.createBufferSource();
    s.buffer = this.noise;
    const f = ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.Q.value = q;
    f.frequency.setValueAtTime(f0, t);
    f.frequency.exponentialRampToValueAtTime(f1, t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol, t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    s.connect(f).connect(g).connect(dest);
    s.start(t, Math.random() * 0.5);
    s.stop(t + dur + 0.05);
  }

  /** A struck bronze bell: inharmonic partials with a long ring and a clapper tick. */
  private bell(dest: AudioNode, f: number, vol: number, delay = 0): void {
    const partials: [number, number, number][] = [[0.5, 0.35, 3.2], [1, 1, 2.6], [2.0, 0.55, 1.8], [2.4, 0.4, 1.4], [3.0, 0.3, 1.1], [4.2, 0.18, 0.8], [5.4, 0.1, 0.6]];
    for (const [ratio, amp, dur] of partials) this.tone(dest, 'sine', f * ratio, f * ratio * 0.998, dur, vol * amp, delay, 0.002);
    this.noiseBurst(dest, 'highpass', 3500, 1, 0.03, vol * 0.5, delay);
  }

  /** Formant-filtered sawtooth "vocal" grunt used for unit acknowledgements. */
  private voice(dest: AudioNode, pitch: number, vowels: [number, number][], dur: number): void {
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(pitch, t);
    o.frequency.linearRampToValueAtTime(pitch * (0.85 + Math.random() * 0.3), t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.25, t + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    const sum = ctx.createGain();
    for (const [f, a] of vowels) {
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = f;
      bp.Q.value = 9;
      const ga = ctx.createGain();
      ga.gain.value = a;
      o.connect(bp).connect(ga).connect(sum);
    }
    sum.connect(g).connect(dest);
    o.start(t);
    o.stop(t + dur + 0.05);
  }

  /**
   * Town Bell: the alarm. A big, low tower bell and its partner clanged fast and urgently (a tocsin),
   * well below the Dock's high double strike (520 Hz) and far quicker.
   */
  townBell(): void {
    const d = this.out('effects');
    if (!d || !this.throttle('townBell', 2500)) return;
    for (let i = 0; i < 10; i++) this.bell(d, i % 2 ? 196 : 233, 0.2 - i * 0.008, i * 0.19);
  }

  /** Back to Work: the all-clear. A slow, calm descending peal of three bells, then a low closing toll. */
  allClear(): void {
    const d = this.out('effects');
    if (!d || !this.throttle('allClear', 2500)) return;
    [392, 330, 262].forEach((f, i) => this.bell(d, f, 0.14, i * 0.6));
    this.bell(d, 196, 0.16, 2.0);
  }

  // ---------- Game-facing API ----------
  ui(kind: 'click' | 'place' | 'error' | 'open'): void {
    const d = this.out('effects');
    if (!d) return;
    if (kind === 'click' || kind === 'open') this.tone(d, 'triangle', 900, 700, 0.06, 0.15);
    else if (kind === 'place') {
      this.tone(d, 'sine', 160, 70, 0.25, 0.5);
      this.noiseBurst(d, 'lowpass', 800, 1, 0.15, 0.3);
    } else {
      this.tone(d, 'square', 220, 200, 0.12, 0.08);
      this.tone(d, 'square', 180, 160, 0.14, 0.08, 0.12);
    }
  }

  private voiceFor(w: World, s: number): { pitch: number; vowels: [number, number][] } {
    const k = KINDS[w.kind[s]].unit;
    const civ = CIVS[w.players[w.owner[s]]?.civ ?? 0]?.key ?? 'english';
    const base = k && k.classes & C.Villager ? 150 : 110;
    const civShift = (civ.charCodeAt(0) % 7) * 6;
    return { pitch: base + civShift + Math.random() * 15, vowels: [[700 + civShift * 5, 1], [1200 + civShift * 8, 0.6], [2500, 0.25]] };
  }

  select(w: World, s: number): void {
    if (!this.throttle('select', 250)) return;
    const k = KINDS[w.kind[s]];
    const d = this.out('voices');
    if (!d) return;
    if (k.unit) {
      const v = this.voiceFor(w, s);
      this.voice(d, v.pitch, v.vowels, 0.22);
    } else if (k.building) {
      this.tone(d, 'triangle', 520, 520, 0.18, 0.12);
      this.tone(d, 'triangle', 780, 780, 0.22, 0.08, 0.06);
    } else this.tone(d, 'sine', 600, 500, 0.08, 0.1);
  }

  acknowledge(w: World, s: number, kind: 'move' | 'attack' | 'work'): void {
    if (s < 0 || !this.throttle('ack', 300)) return;
    const d = this.out('voices');
    if (!d) return;
    const v = this.voiceFor(w, s);
    if (kind === 'attack') {
      this.voice(d, v.pitch * 1.25, [[800, 1], [1300, 0.8], [2600, 0.3]], 0.3);
    } else {
      this.voice(d, v.pitch, v.vowels, 0.16);
      this.voice(d, v.pitch * 0.9, [[500, 1], [900, 0.6], [2400, 0.2]], 0.14);
    }
  }

  onEvents(w: World, events: SimEvent[], local: number): void {
    if (!this.ctx) return;
    for (const e of events) {
      const x = 'x' in e ? (e as { x: number }).x / 1024 : undefined;
      const z = 'y' in e ? (e as { y: number }).y / 1024 : undefined;
      switch (e.e) {
        case 'attack': {
          const atk = KINDS[e.kind].unit?.attack ?? KINDS[e.kind].building?.attack;
          if (!atk) break;
          const key = atk.projectile ?? 'melee';
          if (!this.throttle(`atk-${key}`, key === 'melee' ? 70 : 90)) break;
          const d = this.out('effects', x, z);
          if (!d) break;
          if (!atk.projectile) {
            this.noiseBurst(d, 'bandpass', 3200, 6, 0.12, 0.35);
            this.tone(d, 'square', 2400 + Math.random() * 900, 1800, 0.1, 0.05);
          } else if (key === 'arrow' || key === 'bolt') {
            this.tone(d, 'triangle', 340, 140, 0.12, 0.18);
            this.noiseBurst(d, 'highpass', 4000, 1, 0.08, 0.08);
          } else if (key === 'bullet') {
            this.noiseBurst(d, 'lowpass', 1800, 1, 0.25, 0.55);
            this.tone(d, 'sine', 140, 50, 0.15, 0.4);
          } else if (key === 'ball' || key === 'shell') {
            this.noiseBurst(d, 'lowpass', 500, 1, 0.9, 0.9);
            this.tone(d, 'sine', 70, 30, 0.7, 0.8);
          } else {
            this.tone(d, 'sine', 180, 90, 0.3, 0.3);
            this.noiseBurst(d, 'lowpass', 900, 1, 0.3, 0.2);
          }
          break;
        }
        case 'hit':
          if (e.building && this.throttle('hitb', 120)) {
            const d = this.out('effects', x, z);
            if (d) this.noiseBurst(d, 'lowpass', 600, 2, 0.2, 0.3);
          }
          break;
        case 'death':
          if (e.building) {
            const d = this.out('effects', x, z);
            if (d) {
              this.noiseBurst(d, 'lowpass', 300, 1, 1.8, 0.9);
              this.tone(d, 'sine', 55, 30, 1.5, 0.6);
            }
          } else if (this.throttle('death', 120)) {
            const d = this.out('voices', x, z);
            if (d) this.voice(d, 140 + Math.random() * 40, [[600, 1], [1000, 0.5], [2400, 0.2]], 0.35);
          }
          break;
        case 'gatherTick': {
          if (!this.throttle(`g${e.res}`, 180)) break;
          const d = this.out('effects', x, z);
          if (!d) break;
          if (e.res === 1) this.noiseBurst(d, 'bandpass', 1400, 4, 0.07, 0.25);
          else if (e.res === 2 || e.res === 3) {
            this.tone(d, 'square', 1800 + Math.random() * 400, 1500, 0.05, 0.05);
            this.noiseBurst(d, 'highpass', 3000, 2, 0.05, 0.12);
          } else this.noiseBurst(d, 'bandpass', 900, 2, 0.08, 0.1);
          break;
        }
        case 'spawn': {
          if (e.owner !== local) break;
          const u = KINDS[e.kind].unit;
          if (!u) break;
          if (u.classes & C.Ship) {
            // Harbour bell: the ship's double strike.
            if (!this.throttle('bell', 1500)) break;
            const d = this.out('effects');
            if (d) {
              this.bell(d, 520, 0.16);
              this.bell(d, 520, 0.13, 0.42);
            }
          } else if (u.classes & C.Villager) {
            // A sharp intake of breath, then a quick exhale.
            if (!this.throttle('breath', 600)) break;
            const d = this.out('voices');
            if (d) {
              this.breath(d, 1400, 2600, 2.5, 0.28, 0.32, 0.24);
              this.breath(d, 1300, 600, 1.8, 0.22, 0.4, 0.02, 0.32);
            }
          }
          break;
        }
        case 'built':
          if (e.owner === local) {
            const d = this.out('effects');
            if (d) [523, 659, 784].forEach((f, i) => this.tone(d, 'triangle', f, f, 0.35, 0.12, i * 0.08));
          }
          break;
        case 'age':
          if (e.owner === local) this.fanfare(CIVS[w.players[e.owner].civ].key);
          break;
        case 'underAttack':
          if (e.owner === local && this.throttle('alarm', 4000)) {
            const d = this.out('effects');
            if (d) for (let i = 0; i < 3; i++) this.tone(d, 'square', 660, 640, 0.12, 0.08, i * 0.18);
          }
          break;
        case 'research':
          if (e.owner === local) {
            const d = this.out('effects');
            if (d) this.tone(d, 'sine', 880, 1320, 0.3, 0.12);
          }
          break;
        default:
          break;
      }
    }
  }

  private noteFreq(civ: string, degree: number, octave: number): number {
    const sc = SCALES[civ] ?? SCALES.english;
    const n = sc[((degree % sc.length) + sc.length) % sc.length] + 12 * (octave + Math.floor(degree / sc.length));
    return 220 * Math.pow(2, n / 12);
  }

  fanfare(civ: string): void {
    const d = this.out('music');
    if (!d) return;
    const seq = [0, 2, 4, 7, 4, 7];
    seq.forEach((deg, i) => this.tone(d, 'sawtooth', this.noteFreq(civ, deg, 1), this.noteFreq(civ, deg, 1), 0.4, 0.12, i * 0.16, 0.02));
    this.tone(d, 'triangle', this.noteFreq(civ, 0, 0), this.noteFreq(civ, 0, 0), 1.6, 0.2, 0.9, 0.05);
  }

  victory(won: boolean): void {
    const d = this.out('music');
    if (!d) return;
    const civ = this.musicCiv;
    const seq = won ? [0, 2, 4, 7, 9, 11, 14] : [7, 5, 4, 2, 0, -3];
    seq.forEach((deg, i) => this.tone(d, won ? 'sawtooth' : 'triangle', this.noteFreq(civ, deg, 1), this.noteFreq(civ, deg, 1), 0.6, 0.14, i * 0.28, 0.03));
  }

  /** Start generative music and ambience for a civilisation. */
  startMusic(civ: string): void {
    this.musicCiv = civ;
    this.stopMusic();
    if (!this.ctx) return;
    const step = () => {
      if (!this.ctx) return;
      const d = this.out('music');
      if (d) {
        const bar = this.musicStep++;
        const root = [0, 3, 4, 0, 5, 3, 4, 1][Math.floor(bar / 4) % 8];
        if (bar % 4 === 0) for (const deg of [root, root + 2, root + 4]) this.tone(d, 'triangle', this.noteFreq(civ, deg, -1), this.noteFreq(civ, deg, -1), 3.6, 0.05, 0, 0.6);
        // Sparse melody.
        for (let i = 0; i < 4; i++) {
          if (Math.random() < 0.45) {
            const deg = root + [0, 2, 4, 5, 7][Math.floor(Math.random() * 5)];
            this.tone(d, 'sine', this.noteFreq(civ, deg, 1), this.noteFreq(civ, deg, 1) * 0.998, 0.6, 0.06, i * 0.45, 0.01);
          }
        }
        if (civ === 'japanese' || civ === 'chinese' || civ === 'mongols' || civ === 'aztecs') {
          if (bar % 2 === 0) this.noiseBurst(d, 'lowpass', 180, 2, 0.4, 0.25);
        } else if (bar % 4 === 2) this.noiseBurst(d, 'bandpass', 250, 3, 0.25, 0.12);
      }
      this.musicTimer = window.setTimeout(step, 1800);
    };
    step();
    this.startAmbient();
  }

  stopMusic(): void {
    clearTimeout(this.musicTimer);
    for (const n of this.ambientNodes) {
      try {
        (n as AudioScheduledSourceNode).stop?.();
      } catch {
        /* already stopped */
      }
      n.disconnect();
    }
    this.ambientNodes = [];
  }

  private startAmbient(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const s = ctx.createBufferSource();
    s.buffer = this.noise;
    s.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 400;
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.07;
    const lg = ctx.createGain();
    lg.gain.value = 250;
    lfo.connect(lg).connect(f.frequency);
    const g = ctx.createGain();
    g.gain.value = 0.12;
    s.connect(f).connect(g).connect(this.bus.ambient);
    s.start();
    lfo.start();
    this.ambientNodes.push(s, lfo, f, g);
    // Birdsong.
    const birds = () => {
      if (!this.ctx || !this.ambientNodes.length) return;
      const d = this.bus.ambient;
      const f0 = 2500 + Math.random() * 1500;
      for (let i = 0; i < 2 + Math.floor(Math.random() * 3); i++) this.tone(d, 'sine', f0, f0 * (0.8 + Math.random() * 0.5), 0.09, 0.04, i * 0.12);
      window.setTimeout(birds, 3000 + Math.random() * 6000);
    };
    window.setTimeout(birds, 2000);
  }

  dispose(): void {
    this.stopMusic();
  }
}

export const audio = new AudioEngine();
